<template>
  <div class="video-download">
    <div class="video-download__card">
      <h1 class="video-download__title">{{ displayTitle }}</h1>

      <div v-if="!currentConfig" class="video-download__error">
        無効なURLです。
      </div>

      <template v-else>
        <div class="video-download__player-wrapper">
          <video
            class="video-download__player"
            :src="currentConfig.url"
            controls
            playsinline
            preload="metadata"
          ></video>
        </div>

        <button
          @click="startDownload"
          class="video-download__button"
          :disabled="downloading"
        >
          {{ downloading ? 'ダウンロード中...' : '動画をダウンロード' }}
        </button>

        <p v-if="downloadStarted" class="video-download__done">
          ダウンロードを開始しました。
        </p>
        <p v-if="errorMessage" class="video-download__error">{{ errorMessage }}</p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

// URLクエリパラメータから取得: /video-download?id=xxx
// 未指定の場合はデフォルト(yura-1のメッセージ動画)を表示
const videoId = route.query.id || "yura-1";

const downloading = ref(false);
const downloadStarted = ref(false);
const errorMessage = ref("");

// 動画IDと配信ファイルの対応マップ
// 新しい動画を配布する場合はここにエントリを追加してください
// key: 動画ID (URLのid=で指定), value: { title, url, filename }
const videoConfig = {
  "yura-1": {
    title: "クラファン特典『メッセージ動画』",
    url: "https://storage.googleapis.com/rf-prd-sukisuki-ls.appspot.com/live_station_images_crowdfunding/yura-1/%E3%82%AF%E3%83%A9%E3%83%95%E3%82%A1%E3%83%B3%E7%89%B9%E5%85%B8%E3%80%8E%E3%83%A1%E3%83%83%E3%82%BB%E3%83%BC%E3%82%B7%E3%82%99%E5%8B%95%E7%94%BB%E3%80%8F.mp4",
    filename: "クラファン特典_メッセージ動画.mp4",
  },
};

const currentConfig = computed(() => videoConfig[videoId] || null);

const displayTitle = computed(() => currentConfig.value?.title || "動画");

const startDownload = async () => {
  if (!currentConfig.value) return;

  errorMessage.value = "";
  downloading.value = true;

  try {
    // クロスオリジンでも確実にダウンロードさせるためBlob経由で保存
    const response = await fetch(currentConfig.value.url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = currentConfig.value.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    downloadStarted.value = true;
  } catch (err) {
    console.error("Error downloading video:", err);
    // フォールバック: 直接リンクを開く
    try {
      const link = document.createElement("a");
      link.href = currentConfig.value.url;
      link.download = currentConfig.value.filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      downloadStarted.value = true;
    } catch {
      errorMessage.value = "ダウンロードに失敗しました。";
    }
  } finally {
    downloading.value = false;
  }
};
</script>

<style scoped>
.video-download {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 20px;
}

.video-download__card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 40px 32px;
  max-width: 720px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.video-download__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 24px;
}

.video-download__player-wrapper {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  margin-bottom: 24px;
}

.video-download__player {
  display: block;
  width: 100%;
  max-height: 70vh;
  background: #000;
}

.video-download__button {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
}

.video-download__button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.video-download__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.video-download__done {
  color: #555;
  font-size: 0.9rem;
  margin-top: 16px;
}

.video-download__error {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 16px;
}

@media (max-width: 480px) {
  .video-download__card {
    padding: 28px 16px;
  }

  .video-download__title {
    font-size: 1.25rem;
  }
}
</style>
